import * as v from 'valibot';

import {
  branchSchema,
  cargoTypeSchema,
  carrierCodeSchema,
  carrierSchema,
  carrierServiceSchema,
  currencySchema,
  equipmentTypeSchema,
  pricingTypeSchema,
  tariffTypeSchema,
  transportationModeSchema,
} from '@/modules/entities';

const nullableNumber = v.nullish(v.number());
const nullableString = v.nullish(v.string());

export const pricingApiSchema = v.looseObject({
  branchKey: nullableNumber,
  cargoType: nullableString,
  carrierKey: nullableNumber,
  createdBy: nullableNumber,
  createdOn: nullableString,
  currencyCode: nullableString,
  effectiveDate: nullableString,
  expirationDate: nullableString,
  modifiedBy: nullableNumber,
  modifiedOn: nullableString,
  packingCode: nullableString,
  pricingBasis: nullableString,
  pricingKey: v.number(),
  pricingType: nullableString,
  serviceKey: nullableNumber,
  trailerKey: nullableNumber,
  transportationModeKey: nullableString,
});

export const pricingsApiSchema = v.object({
  items: v.array(pricingApiSchema),
  totalCount: v.number(),
});

export type PricingApi = v.InferOutput<typeof pricingApiSchema>;
export type PricingsApi = v.InferOutput<typeof pricingsApiSchema>;

export const pricingSchema = v.object({
  ...pricingApiSchema.entries,

  branch: v.nullable(branchSchema),
  cargoType: v.nullable(cargoTypeSchema),
  carrier: v.nullable(carrierSchema),
  currency: v.nullable(currencySchema),
  equipmentType: v.nullable(equipmentTypeSchema),
  id: v.string(),
  packingCode: v.nullable(carrierCodeSchema),
  pricingBasis: v.nullable(tariffTypeSchema),
  pricingType: v.nullable(pricingTypeSchema),
  service: v.nullable(carrierServiceSchema),
  transportationMode: v.nullable(transportationModeSchema),
});

export const pricingsSchema = v.object({
  items: v.array(pricingSchema),
  totalCount: v.number(),
});

export type Pricing = v.InferOutput<typeof pricingSchema>;
export type Pricings = v.InferOutput<typeof pricingsSchema>;

/**
 * API contract for GET /api/pricing/pricing/search.
 * Also used to validate URL search params on the pricing search page.
 */
export const pricingsFiltersApiSchema = v.object({
  branchKey: v.optional(v.number()),
  cargoType: v.optional(v.string()),
  carrierKey: v.optional(v.number()),
  currencyCode: v.optional(v.string()),
  effectiveDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  expirationDate: v.optional(v.pipe(v.string(), v.isoTimestamp())),
  isDangerousGoods: v.optional(v.boolean()),
  modeType: v.optional(v.string()),
  packageType: v.optional(v.string()),
  pricingBasis: v.optional(v.string()),
  pricingKey: v.optional(v.number()),
  pricingType: v.optional(v.string()),
  serviceKey: v.optional(v.number()),
  skip: v.optional(v.pipe(v.number(), v.minValue(0)), 0),
  sorting: v.optional(v.string()),
  take: v.optional(v.pipe(v.number(), v.minValue(0)), 10),
  trailerKey: v.optional(v.number()),
});

export type PricingsFiltersApi = v.InferInput<typeof pricingsFiltersApiSchema>;

/**
 * Pricings search filters form
 */
export const pricingsFiltersSchema = v.pipe(
  v.object({
    branch: v.optional(v.nullable(branchSchema)),
    cargoType: v.optional(v.nullable(cargoTypeSchema)),
    carrier: v.optional(v.nullable(carrierSchema)),
    currency: v.optional(v.nullable(currencySchema)),
    effectiveFrom: v.pipe(v.string(), v.nonEmpty('Date is required'), v.isoTimestamp('Please enter a valid date')),
    effectiveTo: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    equipmentType: v.optional(v.nullable(equipmentTypeSchema)),
    packingCode: v.optional(v.nullable(carrierCodeSchema)),
    pricingBasis: v.optional(v.nullable(tariffTypeSchema)),
    pricingKey: v.optional(v.nullable(v.string())),
    pricingType: v.optional(v.nullable(pricingTypeSchema)),
    service: v.optional(v.nullable(carrierServiceSchema)),
    transportationMode: v.optional(v.nullable(transportationModeSchema)),
  }),
  v.forward(
    v.check((data) => {
      if (!data.effectiveTo) {
        return true;
      }

      return new Date(data.effectiveTo) >= new Date(data.effectiveFrom);
    }, 'To date should be bigger than From date'),
    ['effectiveTo'],
  ),
);

export type PricingsFilters = v.InferOutput<typeof pricingsFiltersSchema>;
